import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { oapi, fmtMoney } from '@/lib/officialsApi';
import { ADJ_LABEL, Loading, METHOD_LABEL, Panel } from './PayBits';

const ACTION = {
  pay_rate_changed: 'Rate changed', pay_rate_removed: 'Rate removed', pay_settings_updated: 'Payment settings updated', pay_verified: 'Assignment verified',
  pay_approved: 'Payment approved', pay_unapproved: 'Approval removed', fee_overridden: 'Fee changed', adjustment_added: 'Adjustment added',
  adjustment_voided: 'Adjustment voided', payment_recorded: 'Payment recorded', payment_reversed: 'Payment reversed', batch_created: 'Batch created',
  batch_approved: 'Batch approved', batch_deleted: 'Batch deleted', batch_lines_removed: 'Removed from batch', pay_flags_resolved: 'Review flag cleared',
  finance_change_acknowledged: 'Change to a paid game acknowledged', statement_sent: 'Statements emailed',
  rate_template_saved: 'Rate template saved', rate_template_deleted: 'Rate template deleted', rate_template_applied: 'Rate template applied', pay_rates_repriced: 'Past games re-priced',
};
const TONE = { rate_template_applied: 'bg-[#00247D]', payment_recorded: 'bg-emerald-500', payment_reversed: 'bg-red-500', pay_approved: 'bg-indigo-500', batch_approved: 'bg-indigo-500',
  adjustment_added: 'bg-sky-500', adjustment_voided: 'bg-red-400', finance_change_acknowledged: 'bg-amber-500' };

const SCOPE_LABEL = { default: 'Default', sport: 'Sport', event: 'League', official: 'Official', official_event: 'Official + league' };

const summarize = (d, currency, names) => {
  const m = (v) => fmtMoney(v, currency);
  const num = (v) => typeof v === 'number';
  const bits = [names[d.official_id], d.name];
  if ('to_minor' in d) bits.push(`${num(d.from_minor) ? m(d.from_minor) : 'none'} → ${m(d.to_minor)}`);
  else if (num(d.from_minor)) bits.push(`was ${m(d.from_minor)}`);
  if (num(d.amount_minor)) bits.push(m(d.amount_minor));
  if (num(d.total_minor)) bits.push(`total ${m(d.total_minor)}`);
  if (d.scope) bits.push(`${SCOPE_LABEL[d.scope] || d.scope} rate${d.role_key ? ` · ${d.role_key}` : ''}`);
  if (num(d.repriced)) bits.push(`${d.repriced} past game${d.repriced === 1 ? '' : 's'} re-priced`);
  if (d.unapproved) bits.push(`${d.unapproved} sent back for approval`);
  if (d.adjustments) bits.push(`${d.adjustments} rate adjustment${d.adjustments === 1 ? '' : 's'} (${d.adjustment_minor > 0 ? '+' : ''}${m(d.adjustment_minor)})`);
  if (d.status) bits.push(String(d.status).replace('_', ' '));
  if (d.method) bits.push(METHOD_LABEL[d.method]);
  if (d.type) bits.push(ADJ_LABEL[d.type]);
  if (d.description) bits.push(`"${d.description}"`);
  if (d.fields) bits.push(`changed ${d.fields.join(', ')}`);
  if (num(d.lines)) bits.push(`${d.lines} item${d.lines === 1 ? '' : 's'}`);
  if (num(d.sent)) bits.push(`${d.sent} of ${d.officials} sent`);
  if (d.mode) bits.push(d.mode === 'fill' ? 'filled missing fees' : 'overwrote fees');
  if (num(d.events)) bits.push(`${d.events} league${d.events === 1 ? '' : 's'}`);
  if (num(d.changed)) bits.push(`${d.changed} fee${d.changed === 1 ? '' : 's'} changed`);
  const why = d.reason || d.note || d.manual_reason;
  if (why) bits.push(`Reason: ${why}`);
  return bits.filter(Boolean).join(' · ');
};

export const AuditPanel = ({ orgId, currency, officials, tick }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => { oapi('/officials/payments/audit', { orgId }).then(d => setRows(d.entries)).catch(e => toast.error(e.message)); }, [orgId, tick]);
  const names = Object.fromEntries(officials.map(o => [o.id, o.full_name]));
  return (
    <Panel title="Financial audit trail" desc="Every rate, verification, approval, payment and adjustment - who did it and when. Entries can't be edited." testId="pay-audit">
      {!rows ? <Loading /> : (
        <ol className="divide-y">
          {rows.map(e => (
            <li key={e.id} className="flex gap-3 px-4 py-2.5" data-testid={`audit-entry-${e.id}`}>
              <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${TONE[e.action] || 'bg-slate-300'}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm"><b className="text-slate-800">{ACTION[e.action] || e.action}</b><span className="text-slate-500"> · {e.actor_name}</span></div>
                <div className="text-xs text-slate-500 break-words">{summarize(e.details || {}, currency, names)}</div>
              </div>
              <time className="text-[11px] text-slate-400 whitespace-nowrap">{new Date(e.at).toLocaleString()}</time>
            </li>
          ))}
          {!rows.length && <li className="py-10 text-center text-sm text-slate-500">Nothing recorded yet.</li>}
        </ol>
      )}
    </Panel>
  );
};
